import React, { useState, useEffect } from 'react';
import Dashboard from './components/Dashboard';
import TransactionForm from './components/TransactionForm';
import TransactionList from './components/TransactionList';
import Summary from './components/Summary';

import Header from './components/Header';
import LoginScreen from './components/LoginScreen';
import MonthlyAnalysis from './components/MonthlyAnalysis';
import Subscriptions from './components/Subscriptions';
import Employees from './components/Employees';
import { initializeApp } from "firebase/app";
import { getFirestore, collection, getDocs, addDoc, deleteDoc, doc, setDoc, getDoc } from "firebase/firestore";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};
console.log('FIREBASE CONFIG', firebaseConfig);
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [transactions, setTransactions] = useState([]);
  const [partners, setPartners] = useState([]);
  const [balances, setBalances] = useState({ alfalah: 0, meezan: 0, payoneer: 0 });
  const [summary, setSummary] = useState({});
  const [monthlySummary, setMonthlySummary] = useState([]);
  const [quarterlySummary, setQuarterlySummary] = useState([]);
  const [loading, setLoading] = useState(true);

  const [subscriptions, setSubscriptions] = useState([]);
  const [exchangeRate, setExchangeRate] = useState(365); // GBP to PKR
  const [employees, setEmployees] = useState([]);
  const [leaveRecords, setLeaveRecords] = useState([]);
  const [salaryPayments, setSalaryPayments] = useState([]);

  useEffect(() => {
    // Check if user is already authenticated
    const isAuth = localStorage.getItem('eynvision_auth') === 'true';
    setIsAuthenticated(isAuth);
    if (isAuth) {
      fetchAllData();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    // Fetch transactions
    const txSnap = await getDocs(collection(db, 'transactions'));
    const txs = txSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setTransactions(txs);
    // Fetch partners
    const partnersSnap = await getDocs(collection(db, 'partners'));
    const partnersData = partnersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setPartners(partnersData);
    // Fetch bank balances (deterministic docs: 'alfalah', 'meezan', and 'payoneer')
    let alfDoc = await getDoc(doc(db, 'bank_balance', 'alfalah'));
    let meezDoc = await getDoc(doc(db, 'bank_balance', 'meezan'));
    let payoneerDoc = await getDoc(doc(db, 'bank_balance', 'payoneer'));
    let alfalahBalance = alfDoc.exists() ? (alfDoc.data().balance || 0) : 0;
    let meezanBalance = meezDoc.exists() ? (meezDoc.data().balance || 0) : 0;
    let payoneerBalance = payoneerDoc.exists() ? (payoneerDoc.data().balance || 0) : 0; // Stored in GBP

    // One-time migration: if legacy single balance doc exists, move it to 'alfalah'
    if (!alfDoc.exists()) {
      const legacySnap = await getDocs(collection(db, 'bank_balance'));
      // Find any doc that is not 'meezan' (or just the first one)
      const legacyDoc = legacySnap.docs.find(d => d.id !== 'meezan' && d.id !== 'alfalah') || legacySnap.docs[0];
      if (legacyDoc) {
        const legacyBal = legacyDoc.data().balance || 0;
        await setDoc(doc(db, 'bank_balance', 'alfalah'), { balance: legacyBal }, { merge: true });
        // Optionally keep legacy doc; do not delete to be safe
        alfDoc = await getDoc(doc(db, 'bank_balance', 'alfalah'));
        alfalahBalance = alfDoc.exists() ? (alfDoc.data().balance || 0) : 0;
      }
    }

    setBalances({ alfalah: alfalahBalance, meezan: meezanBalance, payoneer: payoneerBalance });

    // Fetch exchange rate
    const rateDoc = await getDoc(doc(db, 'exchange_rates', 'gbp_to_pkr'));
    if (rateDoc.exists()) {
      setExchangeRate(rateDoc.data().rate || 365);
    }
    // Fetch subscriptions
    const subsSnap = await getDocs(collection(db, 'subscriptions'));
    const subs = subsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setSubscriptions(subs);



    // Fetch employees
    const employeesSnap = await getDocs(collection(db, 'employees'));
    const employeesData = employeesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setEmployees(employeesData);

    // Fetch leave records
    const leaveSnap = await getDocs(collection(db, 'leave_records'));
    const leaveData = leaveSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setLeaveRecords(leaveData);

    // Fetch salary payments
    const salarySnap = await getDocs(collection(db, 'salary_payments'));
    const salaryData = salarySnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setSalaryPayments(salaryData);

    // Calculate summary
    const currentBalances = { alfalah: alfalahBalance, meezan: meezanBalance, payoneer: payoneerBalance };
    setSummary(calculateSummary(txs, partnersData, currentBalances, rateDoc.exists() ? (rateDoc.data().rate || 365) : 365));
    setMonthlySummary(calculateMonthlySummary(txs));
    setQuarterlySummary(calculateQuarterlySummary(txs));
    setLoading(false);
  };

  const addTransaction = async (transactionData) => {
    try {
      const docData = {
        ...transactionData,
        date: new Date().toISOString(),
      };
      if (transactionData.partner_id) {
        docData.partner_name = partners.find(p => p.id === transactionData.partner_id)?.name || '';
      }
      // Handle transfers between banks
      if (transactionData.type === 'transfer') {
        const fromBank = transactionData.transferFrom;
        const toBank = transactionData.transferTo;
        const amountPKR = Number(transactionData.amount);
        const amountGBP = Number(transactionData.amountGBP || 0);

        if (!fromBank || !toBank || fromBank === toBank) {
          throw new Error('Invalid transfer banks');
        }

        // Save transaction as a single transfer record
        const transferRecord = {
          ...docData,
          transferFrom: fromBank,
          transferTo: toBank,
          amountPKR: amountPKR,
          amountGBP: amountGBP,
          exchangeRate: Number(transactionData.exchangeRate || exchangeRate),
          amount: amountPKR // For compatibility/display
        };

        console.log('Saving Transfer Record:', transferRecord);
        await addDoc(collection(db, 'transactions'), transferRecord);

        // Update both bank balances
        const fromDocRef = doc(db, 'bank_balance', fromBank);
        const toDocRef = doc(db, 'bank_balance', toBank);
        const fromDoc = await getDoc(fromDocRef);
        const toDoc = await getDoc(toDocRef);

        let fromBal = fromDoc.exists() ? (fromDoc.data().balance || 0) : 0;
        let toBal = toDoc.exists() ? (toDoc.data().balance || 0) : 0;

        // Deduction from source
        if (fromBank === 'payoneer') {
          fromBal -= amountGBP;
        } else {
          fromBal -= amountPKR;
        }

        // Addition to destination
        if (toBank === 'payoneer') {
          toBal += amountGBP;
        } else {
          toBal += amountPKR;
        }

        await setDoc(fromDocRef, { balance: fromBal }, { merge: true });
        await setDoc(toDocRef, { balance: toBal }, { merge: true });
      } else {
        // Normal transaction to a specific bank
        const bankId = transactionData.bankId || 'alfalah';

        // For Payoneer, handle GBP amounts
        if (bankId === 'payoneer') {
          const amountGBP = Number(transactionData.amountGBP || transactionData.amount);
          const rate = Number(transactionData.exchangeRate || exchangeRate);
          const amountPKR = amountGBP * rate;

          await addDoc(collection(db, 'transactions'), {
            ...docData,
            bankId,
            amountGBP,
            amountPKR,
            exchangeRate: rate,
            amount: amountPKR // For compatibility with existing code
          });

          const bankDocRef = doc(db, 'bank_balance', bankId);
          const bankDoc = await getDoc(bankDocRef);
          let newBalance = bankDoc.exists() ? (bankDoc.data().balance || 0) : 0; // Balance in GBP

          if (transactionData.type === 'expense' || transactionData.type === 'loan_repayment') {
            newBalance -= amountGBP;
          } else if (transactionData.type === 'income' || transactionData.type === 'loan_taken') {
            newBalance += amountGBP;
          }
          await setDoc(bankDocRef, { balance: newBalance }, { merge: true });
        } else {
          // Regular PKR transaction for Alfalah/Meezan
          await addDoc(collection(db, 'transactions'), {
            ...docData,
            bankId
          });
          const bankDocRef = doc(db, 'bank_balance', bankId);
          const bankDoc = await getDoc(bankDocRef);
          let newBalance = bankDoc.exists() ? (bankDoc.data().balance || 0) : 0;
          if (transactionData.type === 'expense' || transactionData.type === 'loan_repayment') {
            newBalance -= Number(transactionData.amount);
          } else if (transactionData.type === 'income' || transactionData.type === 'loan_taken') {
            newBalance += Number(transactionData.amount);
          }
          await setDoc(bankDocRef, { balance: newBalance }, { merge: true });
        }
      }

      await fetchAllData();
    } catch (err) {
      console.error("Failed to add transaction:", err);
      throw err; // So the form shows the error
    }
  };

  const deleteTransaction = async (id) => {
    // Fetch the transaction to be deleted
    const txDocRef = doc(db, 'transactions', id);
    const txSnap = await getDoc(txDocRef);
    if (txSnap.exists()) {
      const tx = txSnap.data();
      // Update balances
      if (tx.type === 'transfer') {
        const amountPKR = Number(tx.amountPKR || tx.amount);
        const amountGBP = Number(tx.amountGBP || 0);
        const fromBank = tx.transferFrom;
        const toBank = tx.transferTo;

        if (fromBank && toBank) {
          const fromDocRef = doc(db, 'bank_balance', fromBank);
          const toDocRef = doc(db, 'bank_balance', toBank);
          const fromDoc = await getDoc(fromDocRef);
          const toDoc = await getDoc(toDocRef);

          let fromBal = fromDoc.exists() ? (fromDoc.data().balance || 0) : 0;
          let toBal = toDoc.exists() ? (toDoc.data().balance || 0) : 0;

          // Revert deduction from source
          if (fromBank === 'payoneer') {
            fromBal += amountGBP;
          } else {
            fromBal += amountPKR;
          }

          // Revert addition to destination
          if (toBank === 'payoneer') {
            toBal -= amountGBP;
          } else {
            toBal -= amountPKR;
          }

          await setDoc(fromDocRef, { balance: fromBal }, { merge: true });
          await setDoc(toDocRef, { balance: toBal }, { merge: true });
        }
      } else {
        const bankId = tx.bankId || 'alfalah';
        const bankDocRef = doc(db, 'bank_balance', bankId);
        const bankDoc = await getDoc(bankDocRef);
        let newBalance = bankDoc.exists() ? (bankDoc.data().balance || 0) : 0;
        
        let txAmount = Number(tx.amount);
        if (bankId === 'payoneer') {
          txAmount = Number(tx.amountGBP || tx.amount);
        }

        if (tx.type === 'expense' || tx.type === 'loan_repayment') {
          newBalance += txAmount; // Revert deduction
        } else if (tx.type === 'income' || tx.type === 'loan_taken') {
          newBalance -= txAmount; // Revert addition
        }
        await setDoc(bankDocRef, { balance: newBalance }, { merge: true });
      }
    }
    // Delete the transaction
    await deleteDoc(txDocRef);
    await fetchAllData();
  };

  const addSubscription = async (subData) => {
    await addDoc(collection(db, 'subscriptions'), subData);
    await fetchAllData();
  };

  const updateSubscription = async (id, subData) => {
    await setDoc(doc(db, 'subscriptions', id), subData, { merge: true });
    await fetchAllData();
  };

  const deleteSubscription = async (id) => {
    await deleteDoc(doc(db, 'subscriptions', id));
    await fetchAllData();
  };

  const markSubscriptionPaid = async (sub, bankId = 'alfalah', alreadyPaid = false) => {
    // Mark as paid for this month
    const now = new Date();
    const paidMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    await updateSubscription(sub.id, { lastPaidMonth: paidMonth });

    // If already paid, don't create transaction or update balance
    if (alreadyPaid) {
      await fetchAllData();
      return;
    }

    const transactionAmountPKR = Number(sub.amount);
    const dateNow = now.toISOString();

    let transactionAmountGBP = 0;
    let currentRate = exchangeRate;

    if (bankId === 'payoneer') {
      transactionAmountGBP = transactionAmountPKR / exchangeRate;
    }

    // Create transaction and update balance
    await addDoc(collection(db, 'transactions'), {
      type: 'expense',
      description: sub.name,
      category: sub.category || 'Subscription',
      date: dateNow,
      bankId: bankId,
      isSubscription: true,
      amount: transactionAmountPKR,
      amountPKR: transactionAmountPKR,
      amountGBP: transactionAmountGBP,
      exchangeRate: currentRate
    });

    // Update bank balance
    const bankDocRef = doc(db, 'bank_balance', bankId);
    const bankDoc = await getDoc(bankDocRef);
    let currentBal = bankDoc.exists() ? (bankDoc.data().balance || 0) : 0;

    let newBalance;
    if (bankId === 'payoneer') {
      newBalance = currentBal - transactionAmountGBP;
    } else {
      newBalance = currentBal - transactionAmountPKR;
    }

    await setDoc(bankDocRef, { balance: newBalance }, { merge: true });

    await fetchAllData();
  };



  // ---- Password Manager API ----


  // ---- Exchange Rate API ----
  const updateExchangeRate = async (newRate) => {
    await setDoc(doc(db, 'exchange_rates', 'gbp_to_pkr'), {
      rate: Number(newRate),
      lastUpdated: new Date().toISOString()
    }, { merge: true });
    setExchangeRate(Number(newRate));
  };

  // ---- Employee Management API ----
  const addEmployee = async (employeeData) => {
    await addDoc(collection(db, 'employees'), {
      ...employeeData,
      createdAt: new Date().toISOString()
    });
    await fetchAllData();
  };

  const updateEmployee = async (id, employeeData) => {
    await setDoc(doc(db, 'employees', id), employeeData, { merge: true });
    await fetchAllData();
  };

  const deleteEmployee = async (id) => {
    await deleteDoc(doc(db, 'employees', id));
    // Optionally delete related leave records and salary payments
    await fetchAllData();
  };

  const addLeaveRecord = async (leaveData) => {
    await addDoc(collection(db, 'leave_records'), {
      ...leaveData,
      createdAt: new Date().toISOString()
    });
    await fetchAllData();
  };


  const recordSalaryPayment = async (paymentData) => {
    const { employeeId, employeeName, month, amount, bankId, alreadyRecorded } = paymentData;

    // Create salary payment record
    const paymentId = `${employeeId}_${month}`;
    const now = new Date().toISOString();

    const paymentRecord = {
      employeeId,
      employeeName,
      month,
      amount: Number(amount),
      status: 'paid',
      paidDate: now,
      bankId: bankId || 'alfalah',
      alreadyRecorded: alreadyRecorded || false
    };

    // Always create/update the salary payment record first
    await setDoc(doc(db, 'salary_payments', paymentId), paymentRecord, { merge: true });

    // If already recorded, we don't need to create a transaction or update bank balance
    if (alreadyRecorded) {
      await fetchAllData();
      return;
    }

    const salaryAmountPKR = Number(amount);
    const bankToUse = bankId || 'alfalah';
    let salaryAmountGBP = 0;

    if (bankToUse === 'payoneer') {
      salaryAmountGBP = salaryAmountPKR / exchangeRate;
    }

    // Create expense transaction
    const transactionRef = await addDoc(collection(db, 'transactions'), {
      type: 'expense',
      amount: salaryAmountPKR,
      amountPKR: salaryAmountPKR,
      amountGBP: salaryAmountGBP,
      exchangeRate: exchangeRate,
      description: `Salary: ${employeeName} (${month})`,
      category: 'Salaries',
      date: now,
      bankId: bankToUse
    });

    // Update payment record with transaction ID
    await setDoc(doc(db, 'salary_payments', paymentId), {
      transactionId: transactionRef.id
    }, { merge: true });

    // Update bank balance
    const bankDocRef = doc(db, 'bank_balance', bankToUse);
    const bankDoc = await getDoc(bankDocRef);
    let currentBal = bankDoc.exists() ? (bankDoc.data().balance || 0) : 0;

    let newBalance;
    if (bankToUse === 'payoneer') {
      newBalance = currentBal - salaryAmountGBP;
    } else {
      newBalance = currentBal - salaryAmountPKR;
    }

    await setDoc(bankDocRef, { balance: newBalance }, { merge: true });

    await fetchAllData();
  };



  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  const handleLogin = () => {
    setIsAuthenticated(true);
    setLoading(true);
    fetchAllData();
  };

  const handleLogout = () => {
    localStorage.removeItem('eynvision_auth');
    setIsAuthenticated(false);
    setActiveTab('dashboard');
  };

  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const totalBalance = (balances.alfalah || 0) + (balances.meezan || 0) + ((balances.payoneer || 0) * exchangeRate);

  return (
    <div className="App">
      <Header
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        balances={balances}
        totalBalance={totalBalance}
        formatCurrency={formatCurrency}
        exchangeRate={exchangeRate}
        onUpdateRate={updateExchangeRate}
      />
      <main className="container" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        <button
          onClick={handleLogout}
          className="btn-danger"
          style={{
            position: 'fixed',
            right: '1.25rem',
            bottom: '1.25rem',
            background: 'var(--danger)',
            color: 'white',
            border: 'none',
            borderRadius: '9999px',
            padding: '0.75rem 1rem',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            zIndex: 1000
          }}
          title="Logout"
        >
          🔒 Logout
        </button>
        {activeTab === 'dashboard' && (
          <Dashboard
            balance={totalBalance}
            summary={summary}
            transactions={transactions}
            monthlySummary={monthlySummary}
            quarterlySummary={quarterlySummary}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            partners={partners}
          />
        )}
        {activeTab === 'add' && (
          <TransactionForm
            partners={partners}
            onTransactionAdded={addTransaction}
            onCancel={() => setActiveTab('dashboard')}
            exchangeRate={exchangeRate}
          />
        )}
        {activeTab === 'transactions' && (
          <TransactionList
            transactions={transactions}
            onDeleteTransaction={deleteTransaction}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        )}
        {activeTab === 'summary' && (
          <Summary
            summary={summary}
            transactions={transactions}
            subscriptions={subscriptions}
            salaryPayments={salaryPayments}
            balances={balances}
            exchangeRate={exchangeRate}
            monthlySummary={monthlySummary}
            formatCurrency={formatCurrency}
          />
        )}
        {activeTab === 'monthly' && (
          <MonthlyAnalysis
            transactions={transactions}
            subscriptions={subscriptions}
            salaryPayments={salaryPayments}
            employees={employees}
            formatCurrency={formatCurrency}
            balance={totalBalance}
            quarterlySummary={quarterlySummary}
          />
        )}
        {activeTab === 'subscriptions' && (
          <Subscriptions
            subscriptions={subscriptions}
            onAdd={addSubscription}
            onUpdate={updateSubscription}
            onDelete={deleteSubscription}
            onMarkPaid={markSubscriptionPaid}
            formatCurrency={formatCurrency}
          />
        )}

        {activeTab === 'employees' && (
          <Employees
            employees={employees}
            leaveRecords={leaveRecords}
            salaryPayments={salaryPayments}
            onAddEmployee={addEmployee}
            onUpdateEmployee={updateEmployee}
            onDeleteEmployee={deleteEmployee}
            onAddLeave={addLeaveRecord}
            onRecordSalary={recordSalaryPayment}
            formatCurrency={formatCurrency}
          />
        )}


      </main>
    </div>
  );
}

function calculateSummary(transactions, partners, balances, currentExchangeRate) {
  const summary = {
    totalIncome: 0,
    totalExpenses: 0,
    totalLoans: 0,
    expensesByCategory: [],
    reconciliation: {
      expectedBalance: 0,
      actualBalance: 0,
      discrepancy: 0
    }
  };
  const loanMap = {};
  const catMap = {};
  transactions.forEach(tx => {
    if (tx.type === 'income' || tx.type === 'loan_repayment') summary.totalIncome += Number(tx.amount);
    if (tx.type === 'expense' || tx.type === 'loan_taken') {
      summary.totalExpenses += Number(tx.amount);
      if (tx.category) {
        catMap[tx.category] = (catMap[tx.category] || 0) + Number(tx.amount);
      }
    }
    if (tx.type === 'loan_taken') {
      summary.totalLoans += Number(tx.amount);
      if (tx.partner_id) {
        loanMap[tx.partner_id] = (loanMap[tx.partner_id] || 0) + Number(tx.amount);
      }
    }
    if (tx.type === 'loan_repayment') {
      summary.totalLoans -= Number(tx.amount);
      if (tx.partner_id) {
        loanMap[tx.partner_id] = (loanMap[tx.partner_id] || 0) - Number(tx.amount);
      }
    }
  });
  summary.loansByPartner = partners.map(p => ({
    name: p.name,
    id: p.id,
    total: loanMap[p.id] || 0
  }));
  summary.expensesByCategory = Object.entries(catMap).map(([category, total]) => ({ category, total }));

  // Reconciliation Calculation
  if (balances && currentExchangeRate) {
    const totalProfit = summary.totalIncome - summary.totalExpenses;
    const actualTotalBalance = (balances.alfalah || 0) + (balances.meezan || 0) + ((balances.payoneer || 0) * currentExchangeRate);

    summary.reconciliation = {
      expectedBalance: totalProfit,
      actualBalance: actualTotalBalance,
      discrepancy: actualTotalBalance - totalProfit
    };
  }

  return summary;
}

function calculateMonthlySummary(transactions) {
  // Group transactions by month and aggregate
  const monthly = {};
  transactions.forEach(tx => {
    const date = new Date(tx.date);
    const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!monthly[month]) {
      monthly[month] = { month, income: 0, expenses: 0, loans: 0 };
    }
    if (tx.type === 'income' || tx.type === 'loan_repayment') monthly[month].income += Number(tx.amount);
    if (tx.type === 'expense' || tx.type === 'loan_taken') monthly[month].expenses += Number(tx.amount);
    if (tx.type === 'loan_taken') monthly[month].loans += Number(tx.amount);
    if (tx.type === 'loan_repayment') monthly[month].loans -= Number(tx.amount);
  });
  // Convert to array and sort by month
  return Object.values(monthly).sort((a, b) => a.month.localeCompare(b.month));
}

function calculateQuarterlySummary(transactions) {
  const quarterly = {};
  transactions.forEach(tx => {
    const date = new Date(tx.date);
    const year = date.getFullYear();
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    const key = `${year}-Q${quarter}`;

    if (!quarterly[key]) {
      quarterly[key] = { label: key, period: key, income: 0, expenses: 0, loans: 0 };
    }

    if (tx.type === 'income' || tx.type === 'loan_repayment') quarterly[key].income += Number(tx.amount);
    if (tx.type === 'expense' || tx.type === 'loan_taken') quarterly[key].expenses += Number(tx.amount);
    if (tx.type === 'loan_taken') quarterly[key].loans += Number(tx.amount);
    if (tx.type === 'loan_repayment') quarterly[key].loans -= Number(tx.amount);
  });

  return Object.values(quarterly).sort((a, b) => a.period.localeCompare(b.period));
}

export default App;